import React, { useState, useEffect } from 'react';
import { 
    Table, 
    Button, 
    Card, 
    Modal, 
    Form, 
    Input, 
    InputNumber, 
    Select, 
    Switch, 
    message, 
    Space, 
    Tag, 
    Typography,
    Divider,
    Popconfirm,
    Row,
    Col
} from 'antd';
import { 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined, 
    GiftOutlined
} from '@ant-design/icons';
import bonusService from '../../services/bonusService';

const { Title, Text } = Typography;
const { Option } = Select;

const BonusManagement = () => {
    const [bonuses, setBonuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingBonus, setEditingBonus] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchBonuses();
    }, []);

    const fetchBonuses = async () => {
        try {
            setLoading(true);
            const response = await bonusService.getAllBonuses();
            if (response.success) {
                setBonuses(response.data);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des bonus');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingBonus(null);
        form.resetFields();
        form.setFieldsValue({ is_active: true });
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingBonus(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await bonusService.deleteBonus(id);
            if (response.success) {
                message.success('Bonus supprimé');
                fetchBonuses();
            }
        } catch (error) {
            message.error('Erreur lors de la suppression');
        }
    };

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            let response;
            if (editingBonus) {
                response = await bonusService.updateBonus(editingBonus.id, values);
            } else {
                response = await bonusService.createBonus(values);
            }

            if (response.success) {
                message.success(editingBonus ? 'Bonus mis à jour' : 'Bonus créé');
                setIsModalVisible(false);
                fetchBonuses();
            }
        } catch (error) {
            console.error('Validation error:', error);
        }
    };

    const columns = [
        {
            title: 'Titre',
            dataIndex: 'title',
            key: 'title',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => {
                if (!type) return <Tag>N/A</Tag>;
                const colors = {
                    deposit: 'blue',
                    referral: 'purple',
                    trade_count: 'orange',
                    trade_count_spot: 'orange',
                    trade_count_futures: 'orange',
                    trade_volume: 'green',
                    trade_volume_spot: 'green',
                    trade_volume_futures: 'green'
                };
                return <Tag color={colors[type] || 'default'}>{type.toString().toUpperCase()}</Tag>;
            }
        },
        {
            title: 'Objectif',
            key: 'target',
            render: (_, record) => (
                <span>{record.requirement_amount || 0} {record.type === 'trade_volume' ? 'USDT' : record.type === 'referral' ? 'Users' : ''}</span>
            )
        },
        {
            title: 'Récompense',
            key: 'reward',
            render: (_, record) => {
                const splitPct = record.internal_split_percent || 0;
                const usdtPct = 100 - splitPct;
                return (
                    <div>
                        <Text type="success" strong>{record.reward_amount || 0} {record.reward_asset || ''}</Text>
                        {splitPct > 0 && (
                            <div style={{ marginTop: 4 }}>
                                <Tag color="blue" style={{ fontSize: 10 }}>{usdtPct}% USDT</Tag>
                                <Tag color="purple" style={{ fontSize: 10 }}>{splitPct}% Crypto</Tag>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Statut',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active) => (
                <Tag color={active ? 'success' : 'error'}>
                    {active ? 'ACTIF' : 'INACTIF'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    <Button 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record)}
                        type="text"
                    />
                    <Popconfirm
                        title="Supprimer ce bonus ?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Oui"
                        cancelText="Non"
                    >
                        <Button 
                            icon={<DeleteOutlined />} 
                            danger 
                            type="text"
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="admin-bonus-inner">
            <div className="page-title-section" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0, color: '#f1f5f9' }}>
                        <GiftOutlined style={{ marginRight: 12, color: '#6366f1' }} />
                        Gestion des Bonus
                    </Title>
                    <Text type="secondary">Configurez les récompenses et les bonus pour vos utilisateurs.</Text>
                </div>
                <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAdd}
                    style={{ borderRadius: 8, height: 40, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none' }}
                >
                    Nouveau Bonus
                </Button>
            </div>
            <Card>
                <Table 
                    columns={columns} 
                    dataSource={bonuses} 
                    rowKey="id" 
                    loading={loading}
                />
            </Card>

            <Modal
                title={editingBonus ? "Modifier le Bonus" : "Créer un Nouveau Bonus"}
                open={isModalVisible}
                onOk={handleModalSubmit}
                onCancel={() => setIsModalVisible(false)}
                width={700}
                okText={editingBonus ? "Mettre à jour" : "Créer"}
                cancelText="Annuler"
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="title"
                                label="Titre du Bonus"
                                rules={[{ required: true, message: 'Le titre est requis' }]}
                            >
                                <Input placeholder="Ex: Bonus de Bienvenue" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label="Type d'objectif"
                                rules={[{ required: true, message: 'Le type est requis' }]}
                            >
                                <Select placeholder="Sélectionnez un type">
                                    <Option value="deposit">Dépôt</Option>
                                    <Option value="referral">Parrainage (Direct)</Option>
                                    <Option value="trade_count">Nombre de Trades</Option>
                                    <Option value="trade_volume">Volume de Trading</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="Description"
                        rules={[{ required: true, message: 'La description est requise' }]}
                    >
                        <Input.TextArea rows={3} placeholder="Expliquez comment obtenir ce bonus" />
                    </Form.Item>

                    <Divider orientation="left">Paramètres de Récompense</Divider>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="requirement_amount"
                                label="Valeur Cible (Objectif)"
                                rules={[{ required: true, message: 'La cible est requise' }]}
                            >
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="reward_amount"
                                label="Montant Récompense"
                                rules={[{ required: true, message: 'Le montant est requis' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="reward_asset"
                                label="Asset principal"
                                initialValue="USDT"
                                rules={[{ required: true, message: 'L\'asset est requis' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Split Crypto Interne</Divider>
                    <Form.Item
                        name="internal_split_percent"
                        label="% versé en Crypto Interne"
                        initialValue={0}
                        help="Ex: 50 = 50% USDT + 50% Crypto interne. 0 = 100% dans l'asset principal."
                    >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="trading_mode"
                                label="Mode Trading (Optionnel)"
                                help="Laissez vide pour Spot + Futures"
                            >
                                <Select allowClear placeholder="Spot ou Futures">
                                    <Option value="spot">Spot uniquement</Option>
                                    <Option value="futures">Futures uniquement</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="is_active"
                                label="Statut"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="ACTIF" unCheckedChildren="INACTIF" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default BonusManagement;
